import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Cpu, Layers, Code2, Zap, Shield, Puzzle, PlayCircle,
  ChevronDown, Boxes, Gauge, ShoppingCart, Package, Server, Factory,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  CodeBox, FeatureCard, ChipGroup, Callout, useDocMeta,
} from '../components/esl/EslShared';

const EslPage = () => {
  useDocMeta(
    'BLOX Core | Biblioteca embebible con API C (BLOX ESL)',
    'BLOX Core es la biblioteca embebible de BLOX ESL. Un host crea la VM, registra funciones C, carga scripts .blox y llama funciones. Datos como TABLAS, vectores, matrices o JSON.'
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 sm:pt-28 pb-16 border-b border-slate-800/60">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage: `url('/esl/levels/fondo_nivel_1.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(4px)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/85 to-slate-950 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium mb-6">
                <Cpu className="w-3 h-3" />
                BLOX ESL · Embeddable Scripting Language
              </div>
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white mb-4 tracking-tight">
                BLOX <span className="text-cyan-400">Core</span>
              </h1>
              <p className="text-lg sm:text-2xl text-slate-300 mb-6 max-w-3xl leading-relaxed">
                La biblioteca <span className="text-white font-semibold">embebible</span> de BLOX ESL: una VM con API C que un host nativo carga para ejecutar scripts <code className="font-mono text-cyan-300">.blox</code>.
              </p>
              <p className="text-slate-400 text-base sm:text-lg max-w-2xl mb-8 leading-relaxed">
                Aplicaciones C/C++, motores como Godot, servicios, simuladores industriales y firmware ESP32-S3. El host mantiene el control del proceso; BLOX aporta reglas y comportamiento modificable sin recompilar.
              </p>

              <div className="flex flex-wrap gap-3">
                <a
                  href="#demos"
                  data-testid="esl-cta-demos"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-cyan-500 text-slate-950 font-semibold hover:bg-cyan-400 transition-colors"
                >
                  <PlayCircle className="w-4 h-4" />
                  Ver demos
                </a>
                <a
                  href="#api"
                  data-testid="esl-cta-api"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-semibold hover:bg-slate-700 transition-colors"
                >
                  <Code2 className="w-4 h-4" />
                  API C
                </a>
              </div>
            </div>

            {/* Mockup desktop */}
            <div className="hidden lg:block lg:col-span-5">
              <div
                className="relative rounded-2xl overflow-hidden border border-cyan-500/20 shadow-2xl shadow-cyan-500/10 aspect-[4/3]"
                data-testid="esl-hero-mockup"
              >
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url('/esl/levels/fondo_nivel_1.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/20 to-slate-950/70" />

                <div className="absolute top-0 left-0 right-0 flex items-center gap-2 px-3 py-2 bg-slate-950/80 backdrop-blur-sm border-b border-cyan-900/40 z-20">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  </div>
                  <span className="text-[10px] text-cyan-400 font-mono ml-1">host.c • reglas.blox</span>
                </div>

                <img src="/esl/player_right_1.png" alt="" className="absolute bottom-6 left-8 w-14 h-14 object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.6)] [image-rendering:pixelated] z-10" loading="lazy" />
                <img src="/esl/gargolas/gargola_viva_perfil.png" alt="" className="absolute bottom-6 right-10 w-14 h-14 object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.6)] [image-rendering:pixelated] z-10" loading="lazy" />
                <img src="/esl/pocion.png" alt="" className="absolute bottom-16 right-1/3 w-8 h-8 object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)] [image-rendering:pixelated] z-10" loading="lazy" />
                <img src="/esl/gems/gem_cyan_on.png" alt="" className="absolute top-1/3 left-1/3 w-6 h-6 object-contain drop-shadow-[0_2px_6px_rgba(0,255,255,0.5)] [image-rendering:pixelated] z-10 animate-pulse" loading="lazy" />

                <div className="absolute bottom-3 left-3 right-3 rounded-lg bg-slate-950/90 backdrop-blur-sm border border-cyan-500/30 shadow-lg z-20">
                  <div className="flex items-center gap-2 px-3 py-1.5 border-b border-cyan-900/40">
                    <Code2 className="w-3 h-3 text-cyan-400" />
                    <span className="text-[10px] text-cyan-400 font-mono">reglas.blox</span>
                  </div>
                  <pre className="p-3 text-[11px] leading-snug font-mono text-slate-300 overflow-hidden">
                    <code>{`FUNCION evaluar_orden(pedido)
INICIO
  SI (pedido[1].score < 700) ENTONCES
    HOST_AUDIT("revision", "..")
  FIN_SI
  RETORNAR resultado
FINAL_FUNCION`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center text-slate-500 text-sm">
            <ChevronDown className="w-5 h-5 mx-auto animate-bounce text-cyan-500/60" />
          </div>
        </div>
      </section>

      {/* FRASE GUÍA */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-2xl sm:text-3xl font-mono text-white leading-relaxed">
            <span className="text-amber-400">C</span> controla el mundo.
            <br />
            <span className="text-cyan-400">BLOX</span> decide la lógica.
          </p>
        </div>
      </section>

      {/* QUÉ ES UN ESL */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">¿Qué es un ESL?</h2>
          <p className="text-slate-400 text-lg max-w-3xl mb-6 leading-relaxed">
            <strong className="text-white">ESL</strong> significa <em>Embeddable Scripting Language</em>: un lenguaje pensado para vivir <strong className="text-white">dentro</strong> de otra aplicación, sin reemplazarla.
          </p>
          <ul className="text-slate-300 space-y-2 text-base leading-relaxed max-w-3xl mb-10">
            <li>• El host C/C++ administra ciclo de vida, archivos, red, UI, sensores, bases de datos o motor gráfico.</li>
            <li>• BLOX define reglas y comportamiento de alto nivel.</li>
            <li>• El host llama funciones BLOX cuando necesita una decisión.</li>
            <li>• BLOX puede llamar funciones nativas registradas por el host.</li>
            <li>• Los datos viajan como números, booleanos, strings, tablas, vectores, matrices y JSON.</li>
          </ul>

          <div className="overflow-x-auto rounded-2xl border border-slate-700/60">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/60">
                  <th className="px-6 py-4 text-left text-slate-400 font-semibold">Lenguaje de consola</th>
                  <th className="px-6 py-4 text-left text-cyan-400 font-semibold">Lenguaje embebible</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {[
                  ['El script suele controlar todo el programa.', 'El host controla el ciclo principal.'],
                  ['Ejecuta main y termina.', 'El host puede llamar funciones muchas veces.'],
                  ['Errores pueden cerrar el proceso.', 'Los errores vuelven al host como estado diagnosticable.'],
                  ['I/O es terminal, archivos o red.', 'I/O puede ser escena, motor, UI, audio o datos del host.'],
                  ['La aplicación se adapta al lenguaje.', 'El lenguaje se adapta a la aplicación.'],
                ].map(([a, b]) => (
                  <tr key={a} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-3 text-slate-400">{a}</td>
                    <td className="px-6 py-3 text-slate-200">{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* POR QUÉ BLOX ESL */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">¿Por qué BLOX ESL?</h2>
          <p className="text-slate-400 text-lg max-w-3xl mb-10 leading-relaxed">
            Pensado para escenarios donde la lógica cambia más rápido que el host:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard icon={Cpu} title="Host-driven">
              Es el host quien decide cuándo y cómo se ejecuta el script. La VM no toma control del proceso.
            </FeatureCard>
            <FeatureCard icon={Puzzle} title="Reglas modificables">
              Comportamiento, umbrales y flujos viven en scripts <code className="font-mono text-cyan-300">.blox</code>. Cambias la regla, no el binario.
            </FeatureCard>
            <FeatureCard icon={Layers} title="Data bridge">
              Datos vía <code className="font-mono text-cyan-300">TABLA</code>, JSON, vectores y matrices. Snapshots sin exponer clases internas.
            </FeatureCard>
            <FeatureCard icon={Shield} title="API C limpia">
              <code className="font-mono text-cyan-300">blox.h</code> integrable desde C, C++, motores y cualquier lenguaje con FFI.
            </FeatureCard>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {['reglas industriales', 'reglas de negocio', 'lógica de videojuegos', 'automatización', 'simuladores', 'validaciones', 'plugins', 'integraciones con JSON', 'prototipos'].map(t => (
              <span key={t} className="px-3 py-1 rounded-full bg-slate-800/60 border border-slate-700/60 text-slate-300 text-xs">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PERFILES */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Perfiles de BLOX</h2>
          <p className="text-slate-400 text-lg max-w-3xl mb-8 leading-relaxed">
            BLOX Core es el motor. Alrededor viven varios perfiles según dónde se aloja el runtime. La <Link to="/cli" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">CLI</Link> es una herramienta derivada del Core.
          </p>

          <div className="overflow-x-auto rounded-2xl border border-slate-700/60">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/60">
                  <th className="px-6 py-4 text-left text-slate-400 font-semibold whitespace-nowrap">Perfil</th>
                  <th className="px-6 py-4 text-left text-slate-400 font-semibold">Para qué sirve</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70 text-slate-300">
                <tr className="bg-cyan-500/5">
                  <td className="px-6 py-3 font-mono text-white font-bold whitespace-nowrap">BLOX Core</td>
                  <td className="px-6 py-3 text-white">Embeber la VM BLOX dentro de un host con <code className="font-mono text-cyan-300">blox.h</code>. <span className="text-xs text-cyan-400 ml-1">← producto principal</span></td>
                </tr>
                {[
                  ['BLOX Core + JSON', 'Embeber BLOX en hosts que intercambian datos con JSON.'],
                  ['BLOX ESP32 Core', 'Usar BLOX como componente ESP-IDF en firmware ESP32-S3.'],
                  ['BLOX Godot', 'Usar BLOX dentro de Godot mediante GDExtension.'],
                ].map(([a, b]) => (
                  <tr key={a} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-3 font-mono text-cyan-300 whitespace-nowrap">{a}</td>
                    <td className="px-6 py-3">{b}</td>
                  </tr>
                ))}
                <tr className="hover:bg-slate-800/30 transition-colors">
                  <td className="px-6 py-3 font-mono text-emerald-300 whitespace-nowrap">BLOX CLI</td>
                  <td className="px-6 py-3">
                    Ejecutar programas <code className="font-mono text-emerald-300">.blox</code> desde terminal usando el mismo lenguaje y runtime.
                    <Link to="/cli" className="ml-2 text-emerald-400 hover:text-emerald-300 text-xs underline underline-offset-2">Ver CLI →</Link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* COMUNICACIÓN BIDIRECCIONAL */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Comunicación bidireccional</h2>
          <p className="text-slate-400 text-lg max-w-3xl mb-10 leading-relaxed">
            La integración tiene dos direcciones. Los datos viajan como <strong className="text-white">números, booleanos, strings, TABLAS, vectores, matrices o JSON</strong> según el perfil usado.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ArrowRight className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 font-semibold">El host llama funciones BLOX</span>
              </div>
              <CodeBox filename="host.c" lang="c">{`blox_vm_call(
    vm,
    "evaluar_orden",
    argumentos,
    1,
    &resultado
);`}</CodeBox>
              <CodeBox filename="reglas.blox" lang="blox">{`FUNCION evaluar_orden(pedido)
INICIO
    // calcular decision
    RETORNAR resultado
FINAL_FUNCION`}</CodeBox>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <ArrowRight className="w-4 h-4 text-cyan-400 rotate-180" />
                <span className="text-cyan-400 font-semibold">BLOX llama funciones C registradas</span>
              </div>
              <CodeBox filename="host.c" lang="c">{`blox_vm_register_native(
    vm,
    "HOST_LOG",
    1,
    BLOX_VALUE_EMPTY,
    host_log,
    NULL
);`}</CodeBox>
              <CodeBox filename="reglas.blox" lang="blox">{`HOST_LOG("regla ejecutada")`}</CodeBox>
            </div>
          </div>

          <Callout tone="cyan" label="Separación de responsabilidades">
            BLOX pide acciones al host mediante callbacks nativos, sin tocar directamente hardware, red, archivos ni UI. El host decide si acepta, valida, encola o rechaza cada acción.
          </Callout>
        </div>
      </section>

      {/* EJEMPLO MÍNIMO EMBEDDING */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Ejemplo mínimo de embedding</h2>
          <p className="text-slate-400 text-lg max-w-3xl mb-8 leading-relaxed">
            Host C crea la VM, registra una función nativa, carga un script, ejecuta <code className="font-mono text-cyan-300">FUNCION PRINCIPAL</code> y llama una función BLOX.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-amber-400 font-semibold text-sm">Host C</span>
              </div>
              <CodeBox filename="host.c" lang="c">{`#include "blox.h"

static BloxStatus host_log(
    BloxVM *vm, const BloxValue *args, size_t argc,
    BloxValue *result, void *user_data
) {
    printf("BLOX dice: %.*s\\n",
        (int)args[0].as.string.length,
        args[0].as.string.data);
    result->type = BLOX_VALUE_EMPTY;
    return BLOX_OK;
}

int main(void) {
    BloxVM *vm = blox_vm_create();
    BloxValue result;

    blox_vm_register_native(vm, "HOST_LOG", 1,
        BLOX_VALUE_EMPTY, host_log, NULL);

    blox_vm_load_file(vm, "programa.blox");
    blox_vm_run_main(vm);
    blox_vm_call(vm, "sumar", NULL, 0, &result);

    if (result.type == BLOX_VALUE_NUMBER)
        printf("resultado = %.0f\\n", result.as.number);

    blox_vm_destroy(vm);
    return 0;
}`}</CodeBox>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-cyan-400" />
                <span className="text-cyan-400 font-semibold text-sm">Script BLOX</span>
              </div>
              <CodeBox filename="programa.blox" lang="blox">{`FUNCION sumar()

FUNCION PRINCIPAL
INICIO
    HOST_LOG("script cargado")
FINAL

FUNCION sumar()
INICIO
    RETORNAR 40 + 2
FINAL_FUNCION`}</CodeBox>

              <Callout tone="emerald" label="Flujo verificado">
                <ol className="list-decimal list-inside space-y-1 text-slate-300">
                  <li>C crea la VM.</li>
                  <li>C registra <code className="font-mono text-emerald-300">HOST_LOG</code>.</li>
                  <li>BLOX llama esa función C.</li>
                  <li>C ejecuta <code className="font-mono text-emerald-300">FUNCION PRINCIPAL</code>.</li>
                  <li>C llama <code className="font-mono text-emerald-300">sumar()</code>.</li>
                  <li>BLOX devuelve <code className="font-mono text-emerald-300">42</code>.</li>
                </ol>
              </Callout>
            </div>
          </div>
        </div>
      </section>

      {/* HOSTS COMPATIBLES */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Hosts compatibles</h2>
          <p className="text-slate-400 text-lg max-w-3xl mb-10 leading-relaxed">
            La API pública es C. BLOX ESL se puede linkar con cualquier host capaz de llamar una API C.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-slate-800/40 border border-slate-700/60">
              <div className="flex items-center gap-2 mb-4">
                <Code2 className="w-5 h-5 text-cyan-400" />
                <h3 className="text-white font-semibold text-lg">C / C++ directos</h3>
              </div>
              <ChipGroup items={['C consola', 'C++ puro', 'Raylib', 'SDL2', 'Qt', 'wxWidgets', 'Unreal (módulo C++)', 'Godot (GDExtension)', 'herramientas propias']} />
            </div>

            <div className="p-6 rounded-2xl bg-slate-800/40 border border-slate-700/60">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-fuchsia-400" />
                <h3 className="text-white font-semibold text-lg">Lenguajes con FFI</h3>
              </div>
              <ChipGroup items={['C#/.NET (P/Invoke)', 'Python (ctypes / cffi)', 'Java/Kotlin (JNI / JNA)', 'Node.js / Electron', 'Rust (bindgen)', 'Go (cgo)']} />
            </div>

            <div className="p-6 rounded-2xl bg-slate-800/40 border border-slate-700/60">
              <div className="flex items-center gap-2 mb-4">
                <Server className="w-5 h-5 text-emerald-400" />
                <h3 className="text-white font-semibold text-lg">Backends y servicios</h3>
              </div>
              <ChipGroup items={['servidor HTTP en C', 'microservicio de reglas', 'FastAPI + DLL', 'Node/Express + DLL', '.NET Web API + DLL']} />
            </div>

            <div className="p-6 rounded-2xl bg-slate-800/40 border border-slate-700/60">
              <div className="flex items-center gap-2 mb-4">
                <Factory className="w-5 h-5 text-amber-400" />
                <h3 className="text-white font-semibold text-lg">Industrial y edge</h3>
              </div>
              <ChipGroup items={['gateways IoT', 'servicios Windows locales', 'simuladores industriales', 'controladores de procesos', 'sistemas de alarma', 'monitoreo']} />
            </div>
          </div>
        </div>
      </section>

      {/* COMPARACIÓN */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">¿Dónde encaja BLOX ESL?</h2>
          <p className="text-slate-400 text-lg max-w-3xl mb-8 leading-relaxed">
            BLOX ESL pertenece a la familia de <em>Lua, JavaScript, Python, Tcl, AngelScript, Squirrel</em>. Apunta a un espacio específico: aplicaciones C/C++ que necesitan reglas <span className="text-white">legibles</span>, tablas, JSON y callbacks nativos, con sintaxis en español y estilo educativo/operacional.
          </p>

          <div className="overflow-x-auto rounded-2xl border border-slate-700/60">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/60">
                  <th className="px-4 py-3 text-left text-slate-400 font-semibold">Lenguaje</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-semibold">Enfoque típico</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70 text-slate-300">
                {[
                  ['Lua', 'Liviano, popular en juegos y mods (WoW addons, Roblox).'],
                  ['JavaScript (V8, QuickJS, Duktape)', 'Plugins de aplicaciones web/desktop, Electron, automatización.'],
                  ['Python embebido', 'Automatización, ciencia de datos, plugins (Blender, CAD, VFX).'],
                  ['Tcl', 'Herramientas EDA, testing, automatización clásica.'],
                  ['Guile / Scheme', 'DSLs, extensión de aplicaciones GNU.'],
                  ['AngelScript', 'Sintaxis parecida a C++ para gameplay en motores propios.'],
                  ['Squirrel', 'Liviano para gameplay y entornos con recursos limitados.'],
                  ['GDScript / Blueprints / GML', 'Scripting dentro de motores concretos (Godot, Unreal, GameMaker).'],
                ].map(([a, b]) => (
                  <tr key={a} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3 font-mono text-cyan-300 whitespace-nowrap">{a}</td>
                    <td className="px-4 py-3">{b}</td>
                  </tr>
                ))}
                <tr className="bg-cyan-500/5">
                  <td className="px-4 py-3 font-mono text-white font-bold whitespace-nowrap">BLOX ESL</td>
                  <td className="px-4 py-3 text-white">
                    Reglas industriales, reglas de negocio, simuladores, juegos 2D, validadores. Español, tablas 1-based, JSON, callbacks nativos.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ARQUITECTURA */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Arquitectura del embedding</h2>
          <p className="text-slate-400 text-lg mb-10">
            Host → VM BLOX → script <code className="text-cyan-400 font-mono">.blox</code> → TABLA de resultados → host aplica cambios.
          </p>

          <div className="rounded-2xl bg-slate-900/60 border border-slate-700/60 p-4 sm:p-6 mb-6">
            <div className="hidden md:flex items-stretch gap-1 lg:gap-2">
              {[
                { title: 'Host', sub: 'C / C++ / motor', color: 'from-amber-500/20 to-amber-500/5', border: 'border-amber-500/40', text: 'text-amber-300' },
                { title: 'BloxVM', sub: 'crea VM, carga script', color: 'from-cyan-500/20 to-cyan-500/5', border: 'border-cyan-500/40', text: 'text-cyan-300' },
                { title: 'script.blox', sub: 'update / evaluar_*', color: 'from-emerald-500/20 to-emerald-500/5', border: 'border-emerald-500/40', text: 'text-emerald-300' },
                { title: 'TABLA', sub: 'estados devueltos', color: 'from-fuchsia-500/20 to-fuchsia-500/5', border: 'border-fuchsia-500/40', text: 'text-fuchsia-300' },
                { title: 'Host aplica', sub: 'render, actuador, DB', color: 'from-amber-500/20 to-amber-500/5', border: 'border-amber-500/40', text: 'text-amber-300' },
              ].map((n, i, arr) => (
                <React.Fragment key={n.title}>
                  <div className={`flex-1 min-w-0 p-3 rounded-lg bg-gradient-to-br ${n.color} border ${n.border}`}>
                    <div className={`text-xs lg:text-sm font-mono font-bold ${n.text} truncate`}>{n.title}</div>
                    <div className="text-[10px] lg:text-xs text-slate-400 mt-1 truncate">{n.sub}</div>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="flex items-center justify-center text-slate-500 shrink-0 px-0.5">
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="md:hidden grid grid-cols-1 gap-2">
              {[
                { title: 'Host', sub: 'C / C++ / motor', color: 'from-amber-500/20 to-amber-500/5', border: 'border-amber-500/40', text: 'text-amber-300' },
                { title: 'BloxVM', sub: 'crea VM, carga script', color: 'from-cyan-500/20 to-cyan-500/5', border: 'border-cyan-500/40', text: 'text-cyan-300' },
                { title: 'script.blox', sub: 'update / evaluar_*', color: 'from-emerald-500/20 to-emerald-500/5', border: 'border-emerald-500/40', text: 'text-emerald-300' },
                { title: 'TABLA', sub: 'estados devueltos', color: 'from-fuchsia-500/20 to-fuchsia-500/5', border: 'border-fuchsia-500/40', text: 'text-fuchsia-300' },
                { title: 'Host aplica', sub: 'render, actuador, DB', color: 'from-amber-500/20 to-amber-500/5', border: 'border-amber-500/40', text: 'text-amber-300' },
              ].map((n) => (
                <div key={n.title} className={`p-3 rounded-lg bg-gradient-to-br ${n.color} border ${n.border}`}>
                  <div className={`text-sm font-mono font-bold ${n.text}`}>{n.title}</div>
                  <div className="text-xs text-slate-400 mt-1">{n.sub}</div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-slate-400 text-sm mb-6">
            Los errores de BLOX <span className="text-white">no cierran el proceso host</span>: vuelven como estado diagnosticable via <code className="text-cyan-400 font-mono">blox_vm_last_error()</code>.
          </p>

          <CodeBox filename="host.c" lang="c">{`BloxVM *vm = blox_vm_create();

blox_vm_register_native(
    vm,
    "HOST_EVENTO",
    2,
    BLOX_VALUE_EMPTY,
    host_evento,
    &estado_del_host
);

blox_vm_load_file(vm, "reglas.blox");
blox_vm_run_main(vm);
blox_vm_call(vm, "evaluar_orden", argumentos, 1, &resultado);

blox_vm_destroy(vm);`}</CodeBox>
        </div>
      </section>

      {/* API C */}
      <section id="api" className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">API C — <code className="font-mono text-cyan-400">blox.h</code></h2>
          <p className="text-slate-400 text-lg max-w-3xl mb-10 leading-relaxed">
            <code className="font-mono text-cyan-400">blox.h</code> es el contrato público del runtime. Tipos opacos, estados de error como enteros, valores universales y funciones sincrónicas.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold text-cyan-400 mb-2">Tipos opacos</h3>
              <CodeBox filename="blox.h" lang="c">{`typedef struct BloxVM BloxVM;
typedef struct BloxTable BloxTable;
typedef struct BloxVector BloxVector;
typedef struct BloxMatrix BloxMatrix;
typedef struct BloxStringVector BloxStringVector;`}</CodeBox>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-cyan-400 mb-2">Estados de error</h3>
              <CodeBox filename="blox.h" lang="c">{`typedef enum BloxStatus {
    BLOX_OK = 0,
    BLOX_ERROR_INVALID_ARGUMENT,
    BLOX_ERROR_IO,
    BLOX_ERROR_PARSE,
    BLOX_ERROR_RUNTIME,
    BLOX_ERROR_NOT_FOUND,
    BLOX_ERROR_UNSUPPORTED
} BloxStatus;`}</CodeBox>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-cyan-400 mb-2">Valor universal</h3>
              <CodeBox filename="blox.h" lang="c">{`typedef struct BloxValue {
    BloxValueType type;
    union {
        double number;
        int    boolean;
        BloxStringView   string;
        BloxTable       *table;
        BloxVector      *vector;
        BloxMatrix      *matrix;
        BloxStringVector *string_vector;
    } as;
} BloxValue;`}</CodeBox>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-cyan-400 mb-2">Registrar función nativa</h3>
              <CodeBox filename="blox.h" lang="c">{`BloxStatus blox_vm_register_native(
    BloxVM *vm,
    const char *name,
    size_t argument_count,
    BloxValueType return_type,
    BloxNativeFunction function,
    void *user_data
);`}</CodeBox>
            </div>
          </div>

          <div className="mt-10 rounded-2xl bg-slate-800/40 border border-slate-700/60 p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Reglas de indexación</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="p-4 rounded-lg bg-slate-950/60 border border-slate-700/50">
                <div className="text-cyan-400 font-mono font-bold mb-2">TABLA y STRING[]</div>
                <div className="text-slate-300">Primer índice = <span className="text-white font-bold">1</span></div>
              </div>
              <div className="p-4 rounded-lg bg-slate-950/60 border border-slate-700/50">
                <div className="text-fuchsia-400 font-mono font-bold mb-2">VECTOR y MATRIZ</div>
                <div className="text-slate-300">Primer índice = <span className="text-white font-bold">0</span></div>
              </div>
            </div>
            <p className="text-slate-400 text-sm mt-4">
              Estas convenciones siguen la semántica interna de BLOX. Los handles devueltos por la VM son <em>prestados</em>: siguen válidos hasta la siguiente llamada a <code className="font-mono text-cyan-300">blox_vm_call()</code> o hasta destruir la VM.
            </p>
          </div>

          <div className="mt-10">
            <h3 className="text-xl font-semibold text-cyan-400 mb-2">Guía rápida de integración</h3>
            <ol className="list-decimal list-inside space-y-1 text-slate-300 text-sm marker:text-cyan-400 marker:font-bold">
              <li>Incluir <code className="font-mono text-cyan-300">blox.h</code>.</li>
              <li>Linkar contra <code className="font-mono text-cyan-300">libblox_core.a</code> o cargar <code className="font-mono text-cyan-300">blox_core.dll</code>.</li>
              <li>Crear una VM con <code className="font-mono text-cyan-300">blox_vm_create()</code>.</li>
              <li>Registrar callbacks nativos con <code className="font-mono text-cyan-300">blox_vm_register_native()</code>.</li>
              <li>Cargar el archivo <code className="font-mono text-cyan-300">.blox</code> con <code className="font-mono text-cyan-300">blox_vm_load_file()</code>.</li>
              <li>Ejecutar <code className="font-mono text-cyan-300">PRINCIPAL</code> con <code className="font-mono text-cyan-300">blox_vm_run_main()</code>.</li>
              <li>Llamar funciones BLOX con <code className="font-mono text-cyan-300">blox_vm_call()</code>.</li>
              <li>Leer resultados con <code className="font-mono text-cyan-300">BloxValue</code>, <code className="font-mono text-cyan-300">blox_table_get()</code> o JSON.</li>
              <li>Destruir la VM con <code className="font-mono text-cyan-300">blox_vm_destroy()</code>.</li>
            </ol>
          </div>
        </div>
      </section>

      {/* DEMOS - 3 CARDS */}
      <section id="demos" className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">Demos integradas</h2>
          <p className="text-slate-400 text-lg max-w-3xl mb-10 leading-relaxed">
            Tres integraciones reales del runtime, cada una con un host C y su archivo <code className="font-mono text-cyan-300">.blox</code>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Demo Godot */}
            <Link
              to="/embedded-scripting-language/demo-godot"
              data-testid="demo-card-godot"
              className="group relative p-6 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/30 hover:border-cyan-400 transition-all hover:-translate-y-1"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center">
                  <PlayCircle className="w-5 h-5 text-cyan-400" />
                </div>
                <span className="px-2 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 text-[10px] font-semibold uppercase tracking-wider">
                  Gaming
                </span>
              </div>
              <div className="text-xs uppercase tracking-wider text-cyan-500 mb-1">Demo 1</div>
              <h3 className="text-xl font-bold text-white mb-2">Juego — Godot</h3>
              <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                Godot 4.6 + GDExtension. <code className="font-mono text-cyan-300">player.blox</code> maneja movimiento, ataque, gárgolas, gemas y transición entre niveles.
              </p>
              <div className="inline-flex items-center gap-2 text-cyan-400 text-sm font-semibold">
                Ver demo
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Demo industrial */}
            <Link
              to="/embedded-scripting-language/demo-industrial"
              data-testid="demo-card-industrial"
              className="group relative p-6 rounded-2xl bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/30 hover:border-amber-400 transition-all hover:-translate-y-1"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                  <Gauge className="w-5 h-5 text-amber-400" />
                </div>
                <span className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-semibold uppercase tracking-wider">
                  Manufactura
                </span>
              </div>
              <div className="text-xs uppercase tracking-wider text-amber-500 mb-1">Demo 2</div>
              <h3 className="text-xl font-bold text-white mb-2">Industrial</h3>
              <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                Motor de reglas para planta. Sensores en C, <code className="font-mono text-amber-300">evaluar_planta()</code> en BLOX decide ventilador, bomba, alarmas y paradas.
              </p>
              <div className="inline-flex items-center gap-2 text-amber-400 text-sm font-semibold">
                Ver demo
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Demo negocios */}
            <Link
              to="/embedded-scripting-language/demo-negocios"
              data-testid="demo-card-negocios"
              className="group relative p-6 rounded-2xl bg-gradient-to-br from-fuchsia-500/10 to-fuchsia-500/5 border border-fuchsia-500/30 hover:border-fuchsia-400 transition-all hover:-translate-y-1"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-fuchsia-500/20 border border-fuchsia-500/40 flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-fuchsia-400" />
                </div>
                <span className="px-2 py-0.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-300 text-[10px] font-semibold uppercase tracking-wider">
                  E-commerce
                </span>
              </div>
              <div className="text-xs uppercase tracking-wider text-fuchsia-500 mb-1">Demo 3</div>
              <h3 className="text-xl font-bold text-white mb-2">Reglas de negocio</h3>
              <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                Motor de reglas para pedidos. <code className="font-mono text-fuchsia-300">evaluar_orden()</code> decide descuentos, impuestos, envío y aprobación.
              </p>
              <div className="inline-flex items-center gap-2 text-fuchsia-400 text-sm font-semibold">
                Ver demo
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ESTADO ACTUAL */}
      <section className="py-16 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Estado actual</h2>
          <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-cyan-500/5 to-fuchsia-500/5 border border-cyan-500/20">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg mb-2">Ciclo funcional completo de embedding</h3>
                <p className="text-slate-300 leading-relaxed mb-3">
                  BLOX ESL tiene funcionando el ciclo end-to-end: cargar runtime, script, inicializar, llamar por frame o por evento, intercambiar tablas y JSON, crear entidades y manejar errores. Las tres demos son la evidencia real de esto.
                </p>
                <p className="text-slate-400 text-sm italic">
                  Una VM activa por proceso, llamadas sincrónicas, API no thread-safe. Decisión intencional para estabilizar primero el contrato embebible.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pt-20 pb-32 sm:pb-24 lg:pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Boxes className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            ¿Quieres seguir el progreso de BLOX ESL?
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Explora la documentación de BLOX y descarga el intérprete actual mientras evoluciona la versión embebible.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/docs"
              data-testid="esl-cta-docs"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-cyan-500 text-slate-950 font-semibold hover:bg-cyan-400 transition-colors"
            >
              <Code2 className="w-4 h-4" />
              Ver Documentación
            </Link>
            <Link
              to="/download"
              data-testid="esl-cta-download"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-semibold hover:bg-slate-700 transition-colors"
            >
              Descargar BLOX
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EslPage;
